import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusPill } from "@/pages/CookDashboard";
import { Building2, Users, Copy } from "lucide-react";

export default function CorporateDashboard() {
  const [me, setMe] = useState(null);
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", billing_email: "" });
  const [joinCode, setJoinCode] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/corporate/me");
      setMe(data);
      if (data.account && data.is_admin) {
        try {
          const d = await api.get("/corporate/dashboard");
          setDash(d.data);
        } catch (err) {
          console.error("Corporate dashboard load failed:", err);
        }
      }
    } catch (err) {
      console.error("Corporate me load failed:", err);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.billing_email) return toast.error("Name & billing email required");
    setBusy(true);
    try {
      await api.post("/corporate/create", form);
      toast.success("Corporate account created");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const join = async () => {
    if (!joinCode.trim()) return toast.error("Paste a join code");
    setBusy(true);
    try {
      const { data } = await api.post("/corporate/join", { join_code: joinCode.trim() });
      toast.success(`Joined ${data.name}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    if (!me?.account?.join_code) return;
    try {
      await navigator.clipboard.writeText(me.account.join_code);
      toast.success("Code copied");
    } catch (err) {
      console.error("Clipboard copy failed:", err);
      toast.error("Copy failed");
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="tick-label">Corporate</div>
        <h1 className="font-display font-black text-5xl tracking-tighter mt-2">Team catering.</h1>

        {!me?.account ? (
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="border hairline p-6">
              <div className="flex items-center gap-2">
                <Building2 size={20} className="text-[#FF3B30]" />
                <div className="font-display text-2xl font-bold">Start a corporate account</div>
              </div>
              <p className="text-neutral-400 text-sm mt-2">One billing email, unlimited team members, one team dashboard tracking every popup and rehearsal.</p>
              <div className="mt-4 space-y-3">
                <input data-testid="corp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Company name" className="w-full border hairline px-3 py-2.5 focus:outline-none focus:border-[#FF3B30]" />
                <input data-testid="corp-email" value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} placeholder="billing@company.com" className="w-full border hairline px-3 py-2.5 focus:outline-none focus:border-[#FF3B30]" />
                <button data-testid="corp-create" onClick={create} disabled={busy} className="btn-signal w-full disabled:opacity-50">Create account</button>
              </div>
            </div>

            <div className="border hairline p-6">
              <div className="flex items-center gap-2">
                <Users size={20} className="text-[#FF3B30]" />
                <div className="font-display text-2xl font-bold">Join a team</div>
              </div>
              <p className="text-neutral-400 text-sm mt-2">Ask your admin for the 8-char join code and paste it below.</p>
              <div className="mt-4 space-y-3">
                <input data-testid="corp-join-code" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="ABC12345" className="w-full border hairline px-3 py-2.5 uppercase font-mono focus:outline-none focus:border-[#FF3B30]" />
                <button data-testid="corp-join" onClick={join} disabled={busy} className="btn-outline w-full disabled:opacity-50">Join</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-10">
            <div className="border hairline p-6 flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="tick-label">You're in</div>
                <div className="font-display text-3xl font-bold mt-1">{me.account.name}</div>
                <div className="text-neutral-500 text-sm">{me.is_admin ? "Admin" : "Member"} · {me.members.length} member(s)</div>
              </div>
              {me.is_admin && (
                <div className="flex items-center gap-2">
                  <span className="tick-label">Invite code</span>
                  <span data-testid="corp-code" className="font-mono text-2xl font-bold text-[#FF3B30]">{me.account.join_code}</span>
                  <button data-testid="corp-copy-code" onClick={copy} className="btn-outline inline-flex items-center gap-2"><Copy size={12} /> Copy</button>
                </div>
              )}
            </div>

            {me.is_admin && dash && (
              <>
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 border hairline">
                  <StatCell label="Members" value={dash.member_count} />
                  <StatCell label="Bookings" value={dash.total_bookings} />
                  <StatCell label="Paid" value={dash.total_paid} />
                  <StatCell label="Total spend" value={`$${dash.total_spend.toFixed(2)}`} highlight />
                </div>

                <div className="mt-8">
                  <div className="tick-label mb-3">All team bookings</div>
                  {dash.bookings.length === 0 ? (
                    <div className="border hairline p-8 text-center text-neutral-500">No bookings yet.</div>
                  ) : (
                    <div className="border hairline overflow-x-auto">
                      <table className="w-full">
                        <thead className="border-b hairline">
                          <tr className="text-left tick-label">
                            <th className="p-3">When</th>
                            <th className="p-3">Cook</th>
                            <th className="p-3">Kitchen</th>
                            <th className="p-3">Total</th>
                            <th className="p-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dash.bookings.map((b) => (
                            <tr key={b.id} className="border-b hairline">
                              <td className="p-3 font-mono text-sm">{new Date(b.start_time).toLocaleString()}</td>
                              <td className="p-3">{b.cook_name}</td>
                              <td className="p-3">{b.kitchen_name}</td>
                              <td className="p-3 font-mono">${b.total.toFixed(2)}</td>
                              <td className="p-3"><StatusPill s={b.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCell({ label, value, highlight }) {
  return (
    <div className="p-5 border-r hairline last:border-r-0">
      <div className="tick-label">{label}</div>
      <div className={`font-mono text-2xl font-bold mt-2 ${highlight ? "text-[#FF3B30]" : ""}`}>{value}</div>
    </div>
  );
}
